import type { TaskStatus } from '@developerz/domain';

// The WORK vocabulary of the Fleet redesign (`docs/design/fleet-redesign/brief.md`
// § Vocabulary, line 50): "task queue, pending, assigned, in progress" are never
// said — the console says WORK, and a row's state is WAITING, STARTING, WORKING.
// The tail of the list keeps the words the catalog already carried, because
// those were already the redesign's ("PR opened", "Ready to merge", "Merged",
// "Failed", "Cancelled" — `fleet.task_status_*`); only the two CI/review rungs
// are renamed into the same register.
//
// ONE TABLE, TYPED OVER THE DOMAIN'S OWN UNION. `satisfies Record<TaskStatus,
// string>` means a `TaskStatus` that gains a member is a COMPILE ERROR here
// rather than a row that renders `⟦fleet.work_status_⟧` on the board — the
// failure the i18n enumeration seam (`i18n.test.ts`) exists to prevent, moved
// onto the type.
//
// This is a key table, not a component: `TaskQueueItem` hands the key to `t()`.
// The keys are literals in the source, so `i18n.coverage.test.ts`'s COMPUTED
// half reads them off the file, and `work-words.test.ts` proves every one of the
// eleven words is reachable.

/**
 * Every task status, exactly one work word. `as const` so `WorkWordKey` is the
 * union of the eleven keys rather than a bare `string`.
 */
export const WORK_WORD_BY_STATUS = {
  pending: 'fleet.work_status_waiting',
  assigned: 'fleet.work_status_starting',
  in_progress: 'fleet.work_status_working',
  completed: 'fleet.task_status_completed',
  awaiting_ci: 'fleet.work_status_checks_running',
  awaiting_review: 'fleet.work_status_in_review',
  mergeable: 'fleet.task_status_mergeable',
  merged: 'fleet.task_status_merged',
  failed: 'fleet.task_status_failed',
  cancelled: 'fleet.task_status_cancelled',
  // #6747: the work word for `paused` is the status label itself.
  paused: 'fleet.task_status_paused',
} as const satisfies Record<TaskStatus, string>;

/** The eleven work words, as the keys that render them. */
export type WorkWordKey = (typeof WORK_WORD_BY_STATUS)[TaskStatus];

/** The translation key for one row's work word. */
export function workWord(status: TaskStatus): WorkWordKey {
  return WORK_WORD_BY_STATUS[status];
}
